import { supabase } from '@/lib/supabase';
import type { Mouvement, MouvementType, MouvementMethode, ZoneStockage } from '@/types/inventory.types';

interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface CreateMovementInput {
  restaurant_id: string;
  produit_id: string;
  type: MouvementType;
  quantite: number;
  unite: string;
  origine?: ZoneStockage | null;
  destination?: ZoneStockage | null;
  utilisateur_id: string;
  methode: MouvementMethode;
  motif?: string | null;
  cout_unitaire?: number | null;
}

/**
 * Service pour la gestion de l'historique des mouvements de stock
 */
class MovementService {
  /**
   * Récupérer tous les mouvements d'un restaurant
   */
  async getMovements(
    restaurantId: string,
    options?: {
      limit?: number;
      offset?: number;
      produitId?: string;
      type?: MouvementType;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<ServiceResponse<Mouvement[]>> {
    try {
      let query = supabase
        .from('mouvements')
        .select(`
          *,
          produits (
            id,
            nom,
            categorie,
            unite
          )
        `)
        .eq('restaurant_id', restaurantId)
        .order('date', { ascending: false });

      // Filtres optionnels
      if (options?.produitId) {
        query = query.eq('produit_id', options.produitId);
      }

      if (options?.type) {
        query = query.eq('type', options.type);
      }

      if (options?.startDate) {
        query = query.gte('date', options.startDate);
      }

      if (options?.endDate) {
        query = query.lte('date', options.endDate);
      }

      // Pagination
      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;

      return {
        success: true,
        data: data as unknown as Mouvement[],
      };
    } catch (error) {
      console.error('Error fetching movements:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer les mouvements d'un produit spécifique
   */
  async getMovementsByProduct(
    restaurantId: string,
    produitId: string,
    limit = 50
  ): Promise<ServiceResponse<Mouvement[]>> {
    return this.getMovements(restaurantId, { produitId, limit });
  }

  /**
   * Créer un nouveau mouvement
   */
  async createMovement(input: CreateMovementInput): Promise<ServiceResponse<Mouvement>> {
    try {
      const { data, error } = await supabase
        .from('mouvements')
        .insert({
          restaurant_id: input.restaurant_id,
          produit_id: input.produit_id,
          date: new Date().toISOString(),
          type: input.type,
          quantite: input.quantite,
          unite: input.unite,
          origine: input.origine || null,
          destination: input.destination || null,
          utilisateur_id: input.utilisateur_id,
          methode: input.methode,
          motif: input.motif || null,
          cout_unitaire: input.cout_unitaire || null,
        })
        .select(`
          *,
          produits (
            id,
            nom,
            categorie,
            unite
          )
        `)
        .single();

      if (error) throw error;

      return {
        success: true,
        data: data as unknown as Mouvement,
      };
    } catch (error) {
      console.error('Error creating movement:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Enregistrer une entrée de stock
   */
  async recordEntry(
    restaurantId: string,
    produitId: string,
    quantite: number,
    unite: string,
    destination: ZoneStockage,
    utilisateurId: string,
    methode: MouvementMethode = 'manuel',
    motif?: string
  ): Promise<ServiceResponse<Mouvement>> {
    return this.createMovement({
      restaurant_id: restaurantId,
      produit_id: produitId,
      type: 'entrée',
      quantite,
      unite,
      destination,
      utilisateur_id: utilisateurId,
      methode,
      motif,
    });
  }

  /**
   * Enregistrer une sortie de stock
   */
  async recordExit(
    restaurantId: string,
    produitId: string,
    quantite: number,
    unite: string,
    origine: ZoneStockage,
    utilisateurId: string,
    methode: MouvementMethode = 'manuel',
    motif?: string
  ): Promise<ServiceResponse<Mouvement>> {
    return this.createMovement({
      restaurant_id: restaurantId,
      produit_id: produitId,
      type: 'sortie',
      quantite,
      unite,
      origine,
      utilisateur_id: utilisateurId,
      methode,
      motif,
    });
  }

  /**
   * Enregistrer un transfert entre zones
   */
  async recordTransfer(
    restaurantId: string,
    produitId: string,
    quantite: number,
    unite: string,
    origine: ZoneStockage,
    destination: ZoneStockage,
    utilisateurId: string,
    methode: MouvementMethode = 'manuel'
  ): Promise<ServiceResponse<Mouvement>> {
    return this.createMovement({
      restaurant_id: restaurantId,
      produit_id: produitId,
      type: 'transfert',
      quantite,
      unite,
      origine,
      destination,
      utilisateur_id: utilisateurId,
      methode,
    });
  }

  /**
   * Enregistrer un ajustement d'inventaire
   */
  async recordAdjustment(
    restaurantId: string,
    produitId: string,
    quantite: number,
    unite: string,
    zone: ZoneStockage,
    utilisateurId: string,
    motif: string
  ): Promise<ServiceResponse<Mouvement>> {
    return this.createMovement({
      restaurant_id: restaurantId,
      produit_id: produitId,
      type: 'ajustement',
      quantite,
      unite,
      destination: zone,
      utilisateur_id: utilisateurId,
      methode: 'manuel',
      motif,
    });
  }

  /**
   * Obtenir des statistiques sur les mouvements
   */
  async getMovementStats(
    restaurantId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ServiceResponse<{
    totalEntries: number;
    totalExits: number;
    totalTransfers: number;
    totalAdjustments: number;
  }>> {
    try {
      let query = supabase
        .from('mouvements')
        .select('type')
        .eq('restaurant_id', restaurantId);

      if (startDate) {
        query = query.gte('date', startDate);
      }

      if (endDate) {
        query = query.lte('date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      const stats = {
        totalEntries: 0,
        totalExits: 0,
        totalTransfers: 0,
        totalAdjustments: 0,
      };

      (data || []).forEach((m) => {
        switch (m.type) {
          case 'entrée':
            stats.totalEntries++;
            break;
          case 'sortie':
            stats.totalExits++;
            break;
          case 'transfert':
            stats.totalTransfers++;
            break;
          case 'ajustement':
          case 'inventaire':
            stats.totalAdjustments++;
            break;
        }
      });

      return {
        success: true,
        data: stats,
      };
    } catch (error) {
      console.error('Error fetching movement stats:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Formater les erreurs
   */
  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return 'Une erreur inattendue est survenue';
  }
}

export const movementService = new MovementService();
